import React, { useState } from 'react'
import { Modal } from 'reactstrap'
import clsx from 'clsx'
import { useIntl } from 'react-intl'

type Props = {
  show: boolean
  onClose: () => void
  onSubmit: (pin: string) => void
  user: any
  error?: string
  loading?: boolean
}

const PinPadModal: React.FC<Props> = ({ show, onClose, onSubmit, user, error, loading }) => {
  const [pin, setPin] = useState('')
  const intl = useIntl()

  const handlePadClick = (val: string) => {
    if (pin.length < 10) {
      setPin(pin + val)
    }
  }

  const handleClear = () => {
    setPin('')
  }

  const handleDelete = () => {
    setPin(pin.slice(0, -1))
  }

  const handleSubmit = () => {
    if (pin.length > 0) {
      onSubmit(pin)
      setPin('') // clear after submit just in case it stays open
    }
  }

  const handleClose = () => {
    setPin('')
    onClose()
  }

  // Handle physical keyboard for kiosk convenience
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!show) return
      
      if (/[0-9]/.test(e.key)) {
        handlePadClick(e.key)
      } else if (e.key === 'Backspace') {
        handleDelete()
      } else if (e.key === 'Enter') {
        handleSubmit()
      } else if (e.key === 'Escape') {
        handleClose()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, pin])

  return (
    <Modal isOpen={show} toggle={handleClose} centered backdrop="static">
      <div className="modal-content">
        <div className="modal-header d-flex justify-content-between align-items-center">
          <h5 className="modal-title">
            {intl.formatMessage({ id: 'KIOSK.PIN_MODAL.TITLE' }, { name: `${user?.name || ''} ${user?.surname || ''}` })}
          </h5>
          <div
            className="btn btn-icon btn-sm btn-active-light-primary ms-2"
            onClick={handleClose}
            aria-label="Close"
          >
            <i className="bi bi-x fs-2"></i>
          </div>
        </div>
        <div className="modal-body pb-0">
          <div className="d-flex flex-column align-items-center mb-5">
            <div className="mb-4">
              <img 
                src={user?.avatarLink || '/media/svg/avatars/blank.svg'} 
                alt="user avatar" 
                className="w-100px h-100px rounded-circle object-fit-cover shadow-sm"
                onError={(e: any) => { e.target.src = '/media/svg/avatars/blank.svg' }}
              />
            </div>
            
            <div className="fs-1 fw-bolder tracking-widest bg-light rounded px-5 py-3 mb-2 min-w-200px text-center shadow-sm" style={{ letterSpacing: '8px', minHeight: '66px' }}>
              {pin.replace(/./g, '•')}
            </div>
            
            {error && (
               <div className="text-danger fw-bold">{error}</div>
            )}
          </div>

          <div className="d-flex flex-column align-items-center">
            <div className="row w-100 g-3 px-4 pb-5 justify-content-center" style={{ maxWidth: '350px' }}>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                <div className="col-4" key={num}>
                  <button 
                    className="btn btn-light-primary w-100 py-4 fs-2 fw-bold shadow-sm" 
                    onClick={() => handlePadClick(num.toString())}
                  >
                    {num}
                  </button>
                </div>
              ))}
              
              <div className="col-4">
                <button 
                  className="btn btn-light-danger w-100 py-4 fs-2 fw-bold shadow-sm"
                  onClick={handleClear}
                >
                  C
                </button>
              </div>
              
              <div className="col-4">
                <button 
                  className="btn btn-light-primary w-100 py-4 fs-2 fw-bold shadow-sm"
                  onClick={() => handlePadClick('0')}
                >
                  0
                </button>
              </div>
              
              <div className="col-4">
                <button 
                  className="btn btn-light-warning w-100 py-4 fs-2 fw-bold shadow-sm" 
                  onClick={handleDelete}
                >
                  <i className="bi bi-backspace-fill fs-2"></i>
                </button>
              </div>

              <div className="col-12 mt-4">
                  <button 
                    className={clsx("btn btn-primary w-100 py-3 fs-3 fw-bold shadow", { "disabled": loading || pin.length === 0 })}
                    onClick={handleSubmit}
                  >
                    {loading ? (
                       <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                    ) : ( 
                       intl.formatMessage({ id: 'KIOSK.LOGIN.TITLE' })
                    )}
                  </button>
              </div>

            </div>
          </div>
        </div>
      </div>
    </Modal>
  )
}

export { PinPadModal }
